// The immersive Git client's LEFT navigator (design 2.2). It replaces the older
// BranchesView with a full ref sidebar: five collapsible sections
// (Local | Remote | Tags | Stashes | Submodules), a single filter box that
// narrows every list, and right-click context menus for the common actions.
//
// Selecting a ref focuses the center graph via `onFilterRev(name | null)`:
// clicking a remote branch or a tag scopes the graph to it; clicking a local
// branch checks it out (the current branch, already checked out, focuses the
// graph instead). Every mutation is guarded by a shared `busy` flag, reloads the
// lists, bubbles `onMutated` so the sibling panes refresh, and surfaces failures
// as an error toast (native git enforces the hard rules; we do not reimplement
// them). Collapsed section state is remembered in localStorage. All `git` access
// is guarded because the capability is optional (desktop-only).

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import type {
  GitApi,
  GitBranch,
  GitBranches,
  GitStash,
  GitSubmodule,
  GitTag,
} from '@slate/core';
import {
  Archive,
  Check,
  ChevronRight,
  Clipboard,
  FolderGit2,
  GitBranch as GitBranchIcon,
  Pencil,
  Plus,
  Tag as TagIcon,
  Trash2,
} from 'lucide-react';
import { useContextMenu, type ContextMenuEntry } from '../../context-menu';
import { toast } from '../../store/toastStore';
import { GitNamePrompt } from '../GitNamePrompt';

export interface GitLeftNavProps {
  /** The repo to operate on, or null when no repo resolves. */
  root: string | null;
  /** The platform GitApi (guarded; undefined when unavailable). */
  git: GitApi | undefined;
  /** Bumped by the client to force a re-read after a cross-pane mutation. */
  refreshToken: number;
  /** Notify the client a mutation happened so the other panes refresh. */
  onMutated: () => void;
  /** Jump the center graph to a commit (a ref's tip): scroll to it and select it. */
  onReveal: (hash: string) => void;
  /** Focus a ref's tip in the graph: dim every commit outside its ancestry. */
  onFocus: (hash: string) => void;
}

/** Which section a collapsed-state entry belongs to. */
type SectionKey = 'local' | 'remote' | 'tags' | 'stashes' | 'submodules';

/** The single active inline name prompt, or null when none is open. */
type Prompt =
  | { kind: 'new-branch' }
  | { kind: 'rename-branch'; name: string }
  | { kind: 'rename-tag'; name: string; target: string };

const COLLAPSE_KEY = 'slate:git:nav:collapsed';

function readCollapsed(): Partial<Record<SectionKey, boolean>> {
  try {
    const raw = localStorage.getItem(COLLAPSE_KEY);
    return raw ? (JSON.parse(raw) as Partial<Record<SectionKey, boolean>>) : {};
  } catch {
    return {};
  }
}

function copyText(text: string): void {
  // navigator.clipboard is undefined in some embeddings; guard it.
  void navigator.clipboard?.writeText(text);
}

export function GitLeftNav({
  root,
  git,
  refreshToken,
  onMutated,
  onReveal,
  onFocus,
}: GitLeftNavProps): ReactElement {
  const [branches, setBranches] = useState<GitBranches | null>(null);
  const [tags, setTags] = useState<GitTag[]>([]);
  const [stashes, setStashes] = useState<GitStash[]>([]);
  const [submodules, setSubmodules] = useState<GitSubmodule[]>([]);
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const [prompt, setPrompt] = useState<Prompt | null>(null);
  const [collapsed, setCollapsed] = useState<Partial<Record<SectionKey, boolean>>>(readCollapsed);
  const { openContextMenu } = useContextMenu();

  // Monotonic request id so a superseded load (repo switch, refreshToken bump,
  // or a post-mutation reload) can never write stale data.
  const reqId = useRef(0);

  const load = useCallback(async (): Promise<void> => {
    if (!root || !git) {
      setBranches(null);
      setTags([]);
      setStashes([]);
      setSubmodules([]);
      return;
    }
    const id = ++reqId.current;
    // Each list loads independently so one empty/erroring source (e.g. a repo
    // with no submodules) never blanks the others.
    const [b, t, s, m] = await Promise.all([
      git.branches(root).catch(() => null),
      git.listTags(root).catch(() => []),
      git.stashList(root).catch(() => []),
      git.submodules(root).catch(() => []),
    ]);
    if (reqId.current !== id) return;
    setBranches(b);
    setTags(t);
    setStashes(s);
    setSubmodules(m);
  }, [root, git]);

  useEffect(() => {
    void load();
  }, [load, refreshToken]);

  const toggle = useCallback((key: SectionKey): void => {
    setCollapsed((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try {
        localStorage.setItem(COLLAPSE_KEY, JSON.stringify(next));
      } catch {
        // Ignore storage failures (private mode / quota); collapse still works in-session.
      }
      return next;
    });
  }, []);

  // Run one mutation: guard against re-entry, reload the lists, bubble onMutated,
  // and surface any failure as an error toast (native git owns the hard rules).
  const runMutation = useCallback(
    async (op: () => Promise<unknown>): Promise<void> => {
      if (busy) return;
      setBusy(true);
      try {
        await op();
        await load();
        onMutated();
      } catch (e) {
        toast(e instanceof Error ? e.message : String(e), { kind: 'error' });
      } finally {
        setBusy(false);
      }
    },
    [busy, load, onMutated],
  );

  const submitPrompt = useCallback(
    (name: string): void => {
      const active = prompt;
      setPrompt(null);
      if (!root || !git || !active) return;
      if (active.kind === 'new-branch') {
        void runMutation(() => git.createBranch(root, name, { checkout: true }));
      } else if (active.kind === 'rename-branch') {
        void runMutation(() => git.renameBranch(root, active.name, name));
      } else {
        // Tags cannot be renamed in place: retag the same target, then drop the old.
        const target = active.target;
        const oldName = active.name;
        void runMutation(async () => {
          await git.createTag(root, name, { rev: target });
          await git.deleteTag(root, oldName);
        });
      }
    },
    [prompt, root, git, runMutation],
  );

  const openLocalMenu = useCallback(
    (e: MouseEvent, branch: GitBranch): void => {
      e.preventDefault();
      e.stopPropagation();
      if (!root || !git) return;
      const items: ContextMenuEntry[] = [];
      if (!branch.head) {
        items.push({
          id: 'checkout',
          label: 'Checkout',
          icon: GitBranchIcon,
          onSelect: () => void runMutation(() => git.checkout(root, branch.name)),
        });
      }
      items.push({
        id: 'focus',
        label: 'Focus in graph',
        icon: GitBranchIcon,
        onSelect: () => onFocus(branch.target),
      });
      items.push({ type: 'separator', id: 'sep1' });
      items.push({
        id: 'rename',
        label: 'Rename…',
        icon: Pencil,
        onSelect: () => setPrompt({ kind: 'rename-branch', name: branch.name }),
      });
      items.push({
        id: 'delete',
        label: 'Delete',
        icon: Trash2,
        danger: true,
        disabled: branch.head,
        onSelect: () => void runMutation(() => git.deleteBranch(root, branch.name, { force: false })),
        altLabel: 'Delete (force)',
        altDanger: true,
        altOnSelect: () => void runMutation(() => git.deleteBranch(root, branch.name, { force: true })),
      });
      items.push({ type: 'separator', id: 'sep2' });
      items.push({
        id: 'copy',
        label: 'Copy branch name',
        icon: Clipboard,
        onSelect: () => copyText(branch.name),
      });
      openContextMenu(e.clientX, e.clientY, items);
    },
    [root, git, runMutation, onFocus, openContextMenu],
  );

  const openRemoteMenu = useCallback(
    (e: MouseEvent, branch: GitBranch): void => {
      e.preventDefault();
      e.stopPropagation();
      const items: ContextMenuEntry[] = [
        {
          id: 'focus',
          label: 'Focus in graph',
          icon: GitBranchIcon,
          onSelect: () => onFocus(branch.target),
        },
        {
          id: 'copy',
          label: 'Copy branch name',
          icon: Clipboard,
          onSelect: () => copyText(branch.name),
        },
      ];
      openContextMenu(e.clientX, e.clientY, items);
    },
    [onFocus, openContextMenu],
  );

  const openTagMenu = useCallback(
    (e: MouseEvent, tag: GitTag): void => {
      e.preventDefault();
      e.stopPropagation();
      if (!root || !git) return;
      const items: ContextMenuEntry[] = [
        {
          id: 'checkout',
          label: 'Checkout (detached)',
          icon: GitBranchIcon,
          onSelect: () => void runMutation(() => git.checkoutCommit(root, tag.name, { detach: true })),
        },
        {
          id: 'focus',
          label: 'Focus in graph',
          icon: TagIcon,
          onSelect: () => onFocus(tag.target),
        },
        { type: 'separator', id: 'sep1' },
        {
          id: 'rename',
          label: 'Rename…',
          icon: Pencil,
          onSelect: () => setPrompt({ kind: 'rename-tag', name: tag.name, target: tag.target }),
        },
        {
          id: 'delete',
          label: 'Delete',
          icon: Trash2,
          danger: true,
          onSelect: () => void runMutation(() => git.deleteTag(root, tag.name)),
        },
        { type: 'separator', id: 'sep2' },
        {
          id: 'copy',
          label: 'Copy tag name',
          icon: Clipboard,
          onSelect: () => copyText(tag.name),
        },
      ];
      openContextMenu(e.clientX, e.clientY, items);
    },
    [root, git, runMutation, onFocus, openContextMenu],
  );

  const openStashMenu = useCallback(
    (e: MouseEvent, stash: GitStash): void => {
      e.preventDefault();
      e.stopPropagation();
      if (!root || !git) return;
      const items: ContextMenuEntry[] = [
        {
          id: 'apply',
          label: 'Apply',
          icon: Archive,
          onSelect: () => void runMutation(() => git.stashApply(root, stash.ref)),
        },
        {
          id: 'pop',
          label: 'Pop',
          icon: Archive,
          onSelect: () => void runMutation(() => git.stashPop(root, stash.ref)),
        },
        { type: 'separator', id: 'sep' },
        {
          id: 'drop',
          label: 'Drop',
          icon: Trash2,
          danger: true,
          onSelect: () => void runMutation(() => git.stashDrop(root, stash.ref)),
        },
      ];
      openContextMenu(e.clientX, e.clientY, items);
    },
    [root, git, runMutation, openContextMenu],
  );

  if (!root) {
    return (
      <div className="mp-git__empty">
        <GitBranchIcon size={24} aria-hidden className="mp-git__empty-icon" />
        <p className="mp-git__empty-title">No Git repository</p>
        <p className="mp-git__empty-note">No Git repository for the active file or workspace.</p>
      </div>
    );
  }

  const needle = filter.trim().toLowerCase();
  const match = (s: string): boolean => needle === '' || s.toLowerCase().includes(needle);

  const local = (branches?.local ?? []).filter((b) => match(b.name));
  const remote = (branches?.remote ?? []).filter((b) => match(b.name));
  const visibleTags = tags.filter((t) => match(t.name));
  const visibleStashes = stashes.filter((s) => match(s.message) || match(s.ref));
  const visibleSubmodules = submodules.filter((s) => match(s.path) || match(s.name));

  return (
    <div className="mp-git-nav">
      <div className="mp-git-nav__filter">
        <input
          type="search"
          className="mp-git-nav__filter-input"
          placeholder="Filter branches, tags…"
          aria-label="Filter branches, tags, stashes and submodules"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </div>

      <div className="mp-git-nav__scroll">
        <NavSection
          id="local"
          title="Local"
          count={local.length}
          collapsed={!!collapsed.local}
          onToggle={() => toggle('local')}
          action={
            <button
              type="button"
              className="mp-iconbtn mp-git-nav__action"
              aria-label="New branch"
              title="New branch"
              disabled={busy}
              onClick={() => setPrompt({ kind: 'new-branch' })}
            >
              <Plus size={13} aria-hidden />
            </button>
          }
        >
          {prompt?.kind === 'new-branch' && (
            <GitNamePrompt
              title="New branch"
              placeholder="Branch name"
              onSubmit={submitPrompt}
              onCancel={() => setPrompt(null)}
            />
          )}
          <ul className="mp-git-nav__list">
            {local.map((b) => (
              <RefRow
                key={`local:${b.name}`}
                label={b.name}
                current={b.head}
                busy={busy}
                icon={b.head ? <Check size={13} aria-hidden /> : null}
                onClick={() => onReveal(b.target)}
                onDoubleClick={b.head ? undefined : () => void runMutation(() => git!.checkout(root, b.name))}
                onContextMenu={(e) => openLocalMenu(e, b)}
              />
            ))}
          </ul>
          {local.length === 0 && <p className="mp-git__clean">No local branches.</p>}
        </NavSection>

        <NavSection
          id="remote"
          title="Remote"
          count={remote.length}
          collapsed={!!collapsed.remote}
          onToggle={() => toggle('remote')}
        >
          <ul className="mp-git-nav__list">
            {remote.map((b) => (
              <RefRow
                key={`remote:${b.name}`}
                label={b.name}
                busy={busy}
                onClick={() => onReveal(b.target)}
                onContextMenu={(e) => openRemoteMenu(e, b)}
              />
            ))}
          </ul>
          {remote.length === 0 && <p className="mp-git__clean">No remote branches.</p>}
        </NavSection>

        <NavSection
          id="tags"
          title="Tags"
          count={visibleTags.length}
          collapsed={!!collapsed.tags}
          onToggle={() => toggle('tags')}
        >
          {prompt?.kind === 'rename-tag' && (
            <GitNamePrompt
              title={`Rename tag ${prompt.name}`}
              placeholder="Tag name"
              initial={prompt.name}
              onSubmit={submitPrompt}
              onCancel={() => setPrompt(null)}
            />
          )}
          <ul className="mp-git-nav__list">
            {visibleTags.map((t) => (
              <RefRow
                key={`tag:${t.name}`}
                label={t.name}
                busy={busy}
                icon={<TagIcon size={13} aria-hidden />}
                onClick={() => onReveal(t.target)}
                onDoubleClick={() => void runMutation(() => git!.checkoutCommit(root, t.name, { detach: true }))}
                onContextMenu={(e) => openTagMenu(e, t)}
              />
            ))}
          </ul>
          {visibleTags.length === 0 && <p className="mp-git__clean">No tags.</p>}
        </NavSection>

        <NavSection
          id="stashes"
          title="Stashes"
          count={visibleStashes.length}
          collapsed={!!collapsed.stashes}
          onToggle={() => toggle('stashes')}
        >
          <ul className="mp-git-nav__list">
            {visibleStashes.map((s) => (
              <RefRow
                key={s.ref}
                label={s.message || s.ref}
                hint={s.ref}
                busy={busy}
                icon={<Archive size={13} aria-hidden />}
                onContextMenu={(e) => openStashMenu(e, s)}
              />
            ))}
          </ul>
          {visibleStashes.length === 0 && <p className="mp-git__clean">No stashes.</p>}
        </NavSection>

        <NavSection
          id="submodules"
          title="Submodules"
          count={visibleSubmodules.length}
          collapsed={!!collapsed.submodules}
          onToggle={() => toggle('submodules')}
        >
          <ul className="mp-git-nav__list">
            {visibleSubmodules.map((s) => (
              <RefRow
                key={`sub:${s.path}`}
                label={s.path}
                hint={s.status}
                busy={busy}
                icon={<FolderGit2 size={13} aria-hidden />}
              />
            ))}
          </ul>
          {visibleSubmodules.length === 0 && <p className="mp-git__clean">No submodules.</p>}
        </NavSection>
      </div>

      {prompt?.kind === 'rename-branch' && (
        <GitNamePrompt
          title={`Rename branch ${prompt.name}`}
          placeholder="Branch name"
          initial={prompt.name}
          onSubmit={submitPrompt}
          onCancel={() => setPrompt(null)}
        />
      )}
    </div>
  );
}

interface NavSectionProps {
  id: SectionKey;
  title: string;
  count: number;
  collapsed: boolean;
  onToggle: () => void;
  action?: ReactNode;
  children: ReactNode;
}

function NavSection({
  id,
  title,
  count,
  collapsed,
  onToggle,
  action,
  children,
}: NavSectionProps): ReactElement {
  const bodyId = `mp-git-nav-${id}`;
  return (
    <section className="mp-git-nav__section" aria-label={title}>
      <div className="mp-git-nav__section-head">
        <button
          type="button"
          className="mp-git-nav__toggle"
          aria-expanded={!collapsed}
          aria-controls={bodyId}
          onClick={onToggle}
        >
          <ChevronRight
            size={12}
            aria-hidden
            className="mp-git-nav__chev"
            data-open={!collapsed || undefined}
          />
          <span className="mp-git-nav__section-title">{title}</span>
          <span className="mp-git-nav__count">{count}</span>
        </button>
        {action}
      </div>
      {!collapsed && (
        <div id={bodyId} className="mp-git-nav__section-body">
          {children}
        </div>
      )}
    </section>
  );
}

interface RefRowProps {
  label: string;
  hint?: string;
  icon?: ReactNode;
  active?: boolean;
  current?: boolean;
  busy: boolean;
  onClick?: () => void;
  onDoubleClick?: () => void;
  onContextMenu?: (e: MouseEvent) => void;
}

function RefRow({
  label,
  hint,
  icon,
  active,
  current,
  busy,
  onClick,
  onDoubleClick,
  onContextMenu,
}: RefRowProps): ReactElement {
  const inner = (
    <>
      <span className="mp-git-nav__marker" aria-hidden>
        {icon}
      </span>
      <span className="mp-git-nav__name" title={label}>
        {label}
      </span>
      {hint && <span className="mp-git-nav__hint">{hint}</span>}
    </>
  );

  const common = {
    className: 'mp-git-nav__row',
    'data-active': active ? '' : undefined,
    'aria-current': current ? ('true' as const) : undefined,
    onContextMenu,
  };

  if (onClick || onDoubleClick) {
    return (
      <li>
        <button
          type="button"
          {...common}
          disabled={busy}
          onClick={onClick}
          onDoubleClick={onDoubleClick}
          title={onDoubleClick ? `${label} (double-click to checkout)` : label}
        >
          {inner}
        </button>
      </li>
    );
  }

  return (
    <li>
      <div {...common}>{inner}</div>
    </li>
  );
}
